import Alert from '@mui/material/Alert'
import Link from '@mui/material/Link'
import KNConfirmationDialog from 'components/KNDialog/KNConfirmationDialog'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { hasTripAcceptance } from './TripAcceptance.helpers'
import type { TripData } from './TripDashboard.types'

export interface TripAcceptanceProps {
  trip: TripData
  onChange?: (updatedTrip: TripData, action: string) => void
  square?: boolean
}

const TripAcceptance = ({
  trip,
  onChange,
  square = false,
}: TripAcceptanceProps): ReactElement | null => {
  const { t } = useTranslation()
  const acceptConfirmationDialog = useDialog<typeof KNConfirmationDialog>({
    onAction: () => {
      onChange?.(trip, 'trip_accepted')
    },
  })
  const declineConfirmationDialog = useDialog<typeof KNConfirmationDialog>({
    onAction: () => {
      onChange?.(trip, 'trip_declined')
    },
  })

  if (!trip || !hasTripAcceptance(trip)) {
    return null
  }

  if (trip.tripAcceptanceStatus === 'ACCEPTED') {
    return (
      <Alert severity="success" elevation={4}>
        {t('screens.cs.trip_details.accept_trip.trip_accepted')}
      </Alert>
    )
  }

  if (trip.tripAcceptanceStatus === 'DECLINED') {
    return (
      <Alert severity="error" elevation={4}>
        {t('screens.cs.trip_details.accept_trip.trip_declined')}
      </Alert>
    )
  }

  return (
    <>
      <Alert severity="info" elevation={4}>
        <Trans
          i18nKey="screens.cs.trip_details.accept_trip.trip_pending"
          components={{
            accept: <Link component="span" underline="always" variant="p4" sx={{ cursor: 'pointer' }}
              onClick={acceptConfirmationDialog.handleOpen} />,
            decline: <Link component="span" underline="always" variant="p4" sx={{ cursor: 'pointer' }}
              onClick={declineConfirmationDialog.handleOpen} />,
          }}
        />
      </Alert>
      <KNConfirmationDialog
        message={t('screens.cs.trip_details.accept_trip.trip_accept_confirmation')}
        continueLabel={t('screens.cs.trip_details.accept_trip.accept')}
        open={acceptConfirmationDialog.isOpen}
        onAction={acceptConfirmationDialog.handleAction}
        onClose={acceptConfirmationDialog.handleClose}
      />
      <KNConfirmationDialog
        message={t('screens.cs.trip_details.accept_trip.trip_decline_confirmation')}
        continueLabel={t('screens.cs.trip_details.accept_trip.decline')}
        open={declineConfirmationDialog.isOpen}
        onAction={declineConfirmationDialog.handleAction}
        onClose={declineConfirmationDialog.handleClose}
      />
    </>
  )
}

export default TripAcceptance
